import React from 'react';
import {useSetAtom} from 'jotai';
import {Outlet, useNavigate} from 'react-router-dom';
import {useLogout} from '@rocket-ui/lk-api';
import {AppLogo} from 'ui';
import {tokenAtom} from 'features/auth/model/authAtom';
import {incrementSessionCount} from 'features/auth/model/sessionCount';
import {AUTHORIZED_MAIN_SCROLL_ID} from 'shared/lib/scrollAuthorizedMainToTop';
import {AuthorizedLayoutNavigation} from './AuthorizedLayoutNavigation';
import {AuthorizedLayoutHeader} from './AuthorizedLayoutHeader';
import {AuthorizedLayoutMobileTopBar} from './AuthorizedLayoutMobileTopBar';

interface AuthorizedLayoutProps {
	showHeader?: boolean;
	headerBackground?: string;
	hideHeaderOnMobile?: boolean;
	headerAlignItems?: 'start' | 'end';
	flushMobileContent?: boolean;
}

export function AuthorizedLayout({
	showHeader = false,
	headerBackground,
	hideHeaderOnMobile = false,
	headerAlignItems,
	flushMobileContent = false,
}: AuthorizedLayoutProps): React.ReactElement {
	const navigate = useNavigate();
	const setTokenDetails = useSetAtom(tokenAtom);
	const {mutateAsync: logout} = useLogout();

	const handleLogout = async () => {
		try {
			await logout();
		} catch {
			// Clear local session even if the API call fails
		}
		incrementSessionCount();
		setTokenDetails(null);
		navigate('/auth/login', {replace: true});
	};

	return (
		<div className={'min-h-screen bg-skin-fill flex flex-col sm:flex-row h-screen'}>
			<aside className={'relative hidden sm:flex sm:flex-col w-[300px] shrink-0 bg-white border-r border-black/10'}>
				<div className={'h-[69px] p-5 flex items-center justify-start border-b border-black/5'}>
					<AppLogo to="/orders" />
				</div>
				<AuthorizedLayoutNavigation variant="sidebar" onLogout={handleLogout} />
			</aside>

			<div className={'flex-1 flex flex-col min-w-0 min-h-0 bg-black/5'}>
				<AuthorizedLayoutMobileTopBar onLogout={handleLogout} />

				{showHeader && (
					<div className={hideHeaderOnMobile ? 'max-sm:hidden' : undefined}>
						<AuthorizedLayoutHeader background={headerBackground} alignItems={headerAlignItems} />
					</div>
				)}

				<main
					id={AUTHORIZED_MAIN_SCROLL_ID}
					className={
						flushMobileContent
							? 'flex-1 min-w-0 px-10 py-8 max-sm:px-0 max-sm:py-0 overflow-y-auto'
							: 'flex-1 min-w-0 px-10 py-8 max-sm:px-4 max-sm:py-6 overflow-y-auto'
					}
				>
					<Outlet />
				</main>
			</div>
		</div>
	);
}
